// Portal-scoped auth utilities (student | teacher | admin)

export const getPortalType = (pathname) => {
  if (pathname && typeof pathname.startsWith === 'function') {
    if (pathname.startsWith('/teacher')) return 'teacher';
    if (pathname.startsWith('/admin')) return 'admin';
    if (pathname.startsWith('/guardian')) return 'guardian';
    if (pathname.startsWith('/student')) return 'student';
  }
  return 'student';
};

// Check if localStorage is available
export const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

const tokenKey = (portal) => `${portal}_token`;
const refreshKey = (portal) => `${portal}_refresh_token`;
const userKey = (portal) => `${portal}_user`;

export const setAuthTokens = (portal, accessToken, refreshToken, userData) => {
  if (!isLocalStorageAvailable()) {
    console.warn('localStorage is not available');
    return;
  }
  try {
    localStorage.setItem(tokenKey(portal), accessToken);
    localStorage.setItem(refreshKey(portal), refreshToken);
    localStorage.setItem(userKey(portal), JSON.stringify(userData || {}));
  } catch (error) {
    console.error('Error setting auth tokens:', error);
    // If localStorage is not available, silently fail
  }
};

export const getAuthToken = (portal) => {
  if (!isLocalStorageAvailable()) return null;
  try {
    return localStorage.getItem(tokenKey(portal));
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

export const getRefreshToken = (portal) => {
  if (!isLocalStorageAvailable()) return null;
  try {
    return localStorage.getItem(refreshKey(portal));
  } catch (error) {
    console.error('Error getting refresh token:', error);
    return null;
  }
};

export const getUserData = (portal) => {
  if (!isLocalStorageAvailable()) return null;
  try {
    const raw = localStorage.getItem(userKey(portal));
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

export const clearAuthTokens = (portal) => {
  if (!isLocalStorageAvailable()) return;
  try {
    localStorage.removeItem(tokenKey(portal));
    localStorage.removeItem(refreshKey(portal));
    localStorage.removeItem(userKey(portal));
    // Clear subject/chapter selection when student logs out
    if (portal === 'student') {
      localStorage.removeItem('student_subject_chapter_selection');
    }
  } catch (error) {
    console.error('Error clearing auth tokens:', error);
    // Continue even if clearing fails
  }
};

export const isAuthenticated = (portal) => {
  return !!(getAuthToken(portal) && getUserData(portal));
};

export const getAuthHeaders = (portal) => {
  const token = getAuthToken(portal);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Check if any user session exists (Student, Teacher, Admin, or Guardian)
export const hasAnySession = () => {
  return isAuthenticated('student') || isAuthenticated('teacher') || isAuthenticated('admin') || isAuthenticated('guardian');
};

// Get the active user's portal type (returns 'student', 'teacher', 'admin', 'guardian', or null)
export const getActivePortal = () => {
  if (isAuthenticated('student')) return 'student';
  if (isAuthenticated('teacher')) return 'teacher';
  if (isAuthenticated('admin')) return 'admin';
  if (isAuthenticated('guardian')) return 'guardian';
  return null;
};

// Get home page path for a portal type
export const getHomePagePath = (portal) => {
  switch (portal) {
    case 'student': return '/student/learn';
    case 'teacher': return '/teacher/dashboard';
    case 'admin': return '/admin/dashboard';
    case 'guardian': return '/guardian/community';
    default: return '/login';
  }
};

// Clear all sessions (all portals)
export const clearAllSessions = () => {
  if (!isLocalStorageAvailable()) return;
  try {
    clearAuthTokens('student');
    clearAuthTokens('teacher');
    clearAuthTokens('admin');
    clearAuthTokens('guardian');
    // Also clear any legacy keys
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('student_board_id');
    localStorage.removeItem('student_grade_id');
    // Clear subject/chapter selection
    localStorage.removeItem('student_subject_chapter_selection');
  } catch (error) {
    console.error('Error clearing all sessions:', error);
    // Continue even if clearing fails
  }
};

// Clear sessions for other portals (keep only the specified portal)
export const clearOtherSessions = (keepPortal) => {
  if (keepPortal !== 'student') clearAuthTokens('student');
  if (keepPortal !== 'teacher') clearAuthTokens('teacher');
  if (keepPortal !== 'admin') clearAuthTokens('admin');
  if (keepPortal !== 'guardian') clearAuthTokens('guardian');
};

// One-time migration if older shared keys were used
export const migrateOldTokens = () => {
  if (!isLocalStorageAvailable()) return;
  try {
    const oldToken = localStorage.getItem('token');
    const oldRefresh = localStorage.getItem('refresh_token');
    const oldUser = localStorage.getItem('user');
    if (oldToken && oldUser) {
      try {
        const data = JSON.parse(oldUser);
        const portal = data?.user_type === 'teacher' ? 'teacher' : data?.user_type === 'admin' ? 'admin' : 'student';
        if (!getAuthToken(portal)) {
          setAuthTokens(portal, oldToken, oldRefresh || '', data);
        }
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
      } catch {}
    }
  } catch (error) {
    console.error('Error migrating old tokens:', error);
  }
};


